import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

/**
 * Shared shimmer backgrounds for loading placeholders (tw-shimmer).
 * Use `SkeletonContainer` as the outer wrapper — it applies
 * `shimmer-container`, which coordinates one diagonal sweep across every
 * descendant bar instead of each element flickering independently.
 *
 * Table-style rows (e.g. settings users) compose `SkeletonContainer` with
 * `SkeletonBox` / `SkeletonLabel` / `SkeletonInput`. For form-like stacks,
 * use `SkeletonField` (label + input placeholders).
 *
 * Every bar pins `text-foreground`: tw-shimmer derives the highlight
 * color from the CSS text color (at 20% alpha), so this keeps the sweep
 * consistent on our dark surfaces regardless of what the parent sets.
 */
const SHIMMER_CLS = "shimmer shimmer-bg text-foreground";

const skeletonToneVariants = cva(SHIMMER_CLS, {
  variants: {
    tone: {
      default: "bg-contrast/8",
      muted: "bg-contrast/6",
    },
  },
  defaultVariants: {
    tone: "default",
  },
});

export type SkeletonToneProps = VariantProps<typeof skeletonToneVariants>;

const SkeletonContainer = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<"div">
>(function SkeletonContainer({ className, ...props }, ref) {
  return (
    <div
      ref={ref}
      data-slot="skeleton-container"
      className={cn("flex shimmer-container", className)}
      {...props}
    />
  );
});

type SkeletonBoxProps = React.ComponentPropsWithoutRef<"div"> &
  SkeletonToneProps;

const SkeletonBox = React.forwardRef<HTMLDivElement, SkeletonBoxProps>(
  function SkeletonBox({ className, tone, ...props }, ref) {
    return (
      <div
        ref={ref}
        data-slot="skeleton-box"
        className={cn(skeletonToneVariants({ tone }), "rounded", className)}
        {...props}
      />
    );
  },
);

type SkeletonLabelProps = React.ComponentPropsWithoutRef<"div"> &
  SkeletonToneProps;

const SkeletonLabel = React.forwardRef<HTMLDivElement, SkeletonLabelProps>(
  function SkeletonLabel({ className, tone, ...props }, ref) {
    return (
      <div
        ref={ref}
        data-slot="skeleton-label"
        className={cn(
          skeletonToneVariants({ tone }),
          "h-3.5 rounded",
          className,
        )}
        {...props}
      />
    );
  },
);

type SkeletonInputProps = React.ComponentPropsWithoutRef<"div"> &
  SkeletonToneProps;

const SkeletonInput = React.forwardRef<HTMLDivElement, SkeletonInputProps>(
  function SkeletonInput({ className, tone, ...props }, ref) {
    return (
      <div
        ref={ref}
        data-slot="skeleton-input"
        className={cn(
          skeletonToneVariants({ tone }),
          "h-5 w-14 rounded-md",
          className,
        )}
        {...props}
      />
    );
  },
);

type SkeletonFieldProps = React.ComponentPropsWithoutRef<"div"> &
  SkeletonToneProps & {
    /** When false, only the input placeholder is shown. Ignored if `children` is set. */
    showLabel?: boolean;
    labelClassName?: string;
    inputClassName?: string;
  };

const SkeletonField = React.forwardRef<HTMLDivElement, SkeletonFieldProps>(
  function SkeletonField(
    {
      className,
      tone,
      showLabel = true,
      labelClassName,
      inputClassName,
      children,
      ...props
    },
    ref,
  ) {
    if (children != null) {
      return (
        <div
          ref={ref}
          data-slot="skeleton-field"
          className={cn("flex flex-col gap-1.5", className)}
          {...props}
        >
          {children}
        </div>
      );
    }

    return (
      <div
        ref={ref}
        data-slot="skeleton-field"
        className={cn("flex flex-col gap-1.5", className)}
        {...props}
      >
        {showLabel ? (
          <SkeletonLabel tone={tone} className={cn("w-24", labelClassName)} />
        ) : null}
        <SkeletonInput
          tone={tone}
          className={cn("h-9 w-full rounded-md", inputClassName)}
        />
      </div>
    );
  },
);

export {
  SkeletonContainer,
  SkeletonBox,
  SkeletonLabel,
  SkeletonInput,
  SkeletonField,
  skeletonToneVariants,
};
